import React, { useState, useEffect, memo, useContext, useMemo } from "react";
import {
  Heading,
  Pill,
  Button,
  Text,
  TextField,
  Loading,
  Select,
  SkeletonBase,
  toast,
} from "@foris/avocado-suite";
import useGetEventTypes from "../../hooks/useGetEventTypes";
import * as utils from "../../utils";
import useClassroomBookingsMutation from "../../hooks/useClassroomBookingsMutation";
import ProgramSelect from "../AsyncSelectors/ProgramAsyncSelect";
import UserSelect from "../AsyncSelectors/UserAsyncSelect";
import { IParams } from "@models/IParams";
import { useParams, useHistory } from "react-router-dom";
import { ClassroomBooking } from "@models/ISchema";
import { BookingContext } from "@modules/booking/context/BookingContext";
import { useTranslation } from "react-i18next";
import { Types } from "../../context/request.reducer";
import { Types as SearchTypes } from "../../context/search.reducer";
import { DayRowData } from "@modules/booking/context/types";
import useSelectedWeekIntervals from "@modules/booking/hooks/useSelectedWeekIntervals";
import { getProgramSelectorLabel } from "@modules/booking/utils/programs";
import { buildBookingPreferences } from "@modules/booking/utils/preferences";
import { ContextEDH } from "@context/ContextEDH";
import Textarea from "@common/components/Textarea";
import css from "./detail.module.scss";
import useBookingAccess from "@common/hooks/useBookingAccess";
import { deduplicateDateTime, hasMutationResult } from "./detail.utils";

interface Props {
  booking: ClassroomBooking;
  editing: boolean;
  isLoadingDetails?: boolean;
  setEdit: (value: boolean) => void;
}

interface SelectableOption {
  value: string;
  label: string;
}

const Detail: React.FC<Props> = ({ booking, editing, setEdit, isLoadingDetails }) => {
  const history = useHistory();
  const { workspace, origin, scenario }: IParams = useParams();
  const contextUrl = `${workspace}/${scenario}/${origin}`;
  const { state, dispatch } = useContext(BookingContext);
  const {
    state: { base },
  } = useContext(ContextEDH);
  const [, getBookingTypes] = useGetEventTypes();
  const [bookingTitle, setBookingTitle] = useState<string>(booking?.title);
  const [bookingDescription, setBookingDescription] = useState<string>(booking?.description);
  const [descriptionHasBeenChanged, setDescriptionHasBeenChanged] = useState<boolean>(false);
  const [ignoreMutationResult, setIgnoreMutationResult] = useState<boolean>(false);
  const [mutationResult, submitClassroomBookingsMutation] = useClassroomBookingsMutation({
    origin,
    scenario,
  });
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const { isAdminBooking } = useBookingAccess();

  const [selectedWeeksInterval] = useSelectedWeekIntervals(state?.search?.currentBooking?.sessions);

  const [selectableBookingTypeByIds, setSelectableBookingTypeByIds] = useState<
    Record<string, SelectableOption>
  >({});

  useEffect(() => {
    if (ignoreMutationResult || isLoadingDetails || isLoading) {
      return;
    }

    const { result, error } = mutationResult;

    if (!hasMutationResult(result)) return;

    const booking = result?.payload?.updates[0]?.booking;

    if (!error && Boolean(booking)) {
      setIgnoreMutationResult(true);

      // cleaning
      dispatch({ type: SearchTypes.ClearSearch, payload: null });
      dispatch({ type: Types.ClearRequest, payload: null });

      dispatch({ type: SearchTypes.SetCurrentBooking, payload: booking });
      dispatch({ type: SearchTypes.SetView, payload: "search" });

      // `ClearSearch` wipes `preferences`, and the mount-only effect that
      // repopulates them won't re-run (same booking id, no remount). Re-set
      // the building/program here so the edition view shows the updated values.
      dispatch({
        type: SearchTypes.SetPreferences,
        payload: buildBookingPreferences(booking, state?.search?.preferences),
      });
      dispatch({
        type: Types.SetSnackbar,
        payload: { display: true, message: "Detalles actualizados exitosamente", error: false },
      });
      dispatch({
        type: Types.SetUserResponsible,
        payload: {
          value: booking?.responsible?.id,
          label: `${booking?.responsible?.name} ${booking?.responsible?.lastName}`,
          id: booking?.responsible?.id,
          error: false,
        },
      });
      dispatch({ type: Types.SetEventType, payload: { value: booking?.type?.id, error: false } });
      setBookingTitle(booking?.title || "");
      setBookingDescription(booking?.description || "");
      setDescriptionHasBeenChanged(false);
      setEdit(false);

      // redirect to the created booking (remember that booking updates
      // deletes the edited booking and creates a new one)
      history.replace(`/booking/detail/${contextUrl}/${booking?.id}`);
    } else {
      setIgnoreMutationResult(true);

      dispatch({
        type: Types.SetSnackbar,
        payload: {
          display: true,
          message: "Ocurrió un error. Detalles no actualizados",
          error: true,
        },
      });
    }
  }, [mutationResult]);

  useEffect(() => {
    if (isLoadingDetails) return;

    getBookingTypes();

    dispatch({
      type: Types.SetUserResponsible,
      payload: {
        value: booking?.responsible?.id,
        label: `${booking?.responsible?.name} ${booking?.responsible?.lastName}`,
        id: booking?.responsible?.id,
        error: false,
      },
    });
    dispatch({ type: Types.SetEventType, payload: { value: booking?.type?.id, error: false } });
  }, [isLoadingDetails]);

  useEffect(() => {
    if (!state?.request?.eventTypesOptions?.length || isLoadingDetails) return;

    setSelectableBookingTypeByIds(
      state?.request?.eventTypesOptions?.reduce(
        (acc: Record<string, SelectableOption>, bookingType: any) => {
          acc[bookingType?.id] = {
            value: bookingType?.id,
            label: bookingType?.label,
            ...bookingType,
          };
          return acc;
        },
        {},
      ),
    );
  }, [state?.request?.eventTypesOptions, isLoadingDetails]);

  /**
   * Update the edited booking after any change
   */
  useEffect(() => {
    if (isLoadingDetails) return;

    const editedBooking = {
      ...(state?.request?.editedBooking ?? {}),
      title: bookingTitle,
      responsible: state?.request?.userResponsible,
      ...(descriptionHasBeenChanged ? { description: bookingDescription } : {}),
      type: selectableBookingTypeByIds[state?.request?.eventType?.value],
      program: state?.search?.preferences?.program?.value,
    } as unknown as ClassroomBooking;

    dispatch({ type: Types.SetEditedBooking, payload: editedBooking });
  }, [
    isLoadingDetails,
    bookingTitle,
    bookingDescription,
    state?.request?.eventType,
    state?.request?.userResponsible,
    state?.search?.preferences?.program,
    state?.search?.preferences?.building,
  ]);
  /**
   * When the user wants to edit a booking, the callback inside this `useEffect`
   * enrich the edition form with the data of the booking to edit.
   */
  useEffect(() => {
    if (!state?.search?.currentBooking || isLoadingDetails) return;

    const formatTime = (time: string) => {
      const [hour, min] = time.split(":");
      return hour + ":" + min;
    };

    const sessions = (state?.search?.currentBooking?.sessions ?? []).map(session =>
      session?.scheduleEvent?.[0] ? session?.scheduleEvent?.[0] : null,
    );

    const newDateTime: DayRowData[] = sessions.reduce((acc: any, event: any, index: number) => {
      const [[date, rawTime], [, rawEndTime]] = [
        event?.start?.split(" ") ?? ["", ""],
        event?.end?.split(" ") ?? ["", ""],
      ];
      const startTime = formatTime(rawTime);
      const endTime = formatTime(rawEndTime);

      return [
        ...acc,
        {
          index,
          recurrence: state?.request?.editedBooking?.isRecurrent,
          day: { error: false, value: date },
          start: { error: false, value: { label: startTime, value: startTime } },
          end: { error: false, value: { label: endTime, value: endTime } },
        },
      ];
    }, []);

    dispatch({ type: SearchTypes.SetDateTime, payload: deduplicateDateTime(newDateTime) });

    // set the sessions and responsible of the editedBooking
    dispatch({
      type: Types.SetEditedBooking,
      payload: {
        ...state?.request?.editedBooking,
        sessions: state?.search?.currentBooking?.sessions,
        responsible: state?.search?.currentBooking?.responsible,
        capacity: state?.search?.currentBooking?.capacity,
      },
    });

    // set the selectedWeeks of the current editedBooking
    dispatch({
      type: SearchTypes.SetIntervals,
      payload: (state?.search?.intervals ?? [])?.map(week => ({
        ...week,
        selected: week?.id in selectedWeeksInterval,
      })),
    });
  }, [state?.search?.currentBooking]);

  useEffect(() => {
    // set building and program on preferences
    dispatch({
      type: SearchTypes.SetPreferences,
      payload: buildBookingPreferences(booking, state?.search?.preferences),
    });
  }, []);

  const handleSaveChanges = async () => {
    const program = state?.search?.preferences?.program?.value;

    if (isLoading) return;
    if (base?.bookingsProgramRequired === "REQUIRED" && !program) {
      const clonePreferences = { ...state?.search?.preferences };
      clonePreferences.program = {
        label: null,
        value: null,
        error: true,
      };

      dispatch({
        type: SearchTypes.SetPreferences,
        payload: clonePreferences,
      });
      return;
    }

    setIsLoading(true);
    setIgnoreMutationResult(false);
    await submitClassroomBookingsMutation(state?.request?.editedBooking, false, true, true);
    setIsLoading(false);
  };

  const hasChanges = useMemo(() => {
    if (!editing) return false;

    if (bookingTitle !== booking?.title) return true;
    if (bookingDescription !== booking?.description) return true;
    if (state?.request?.eventType?.value !== booking?.type?.id) return true;
    if (state?.search?.preferences?.program?.value !== booking?.relatedProgram?.id) return true;
    if (state?.request?.userResponsible?.id !== booking?.responsible?.id) return true;

    return false;
  }, [
    editing,
    bookingTitle,
    bookingDescription,
    state?.request?.eventType,
    state?.search?.preferences?.program,
    state?.request?.userResponsible,
    booking,
  ]);

  useEffect(() => {
    if (state?.request?.snackbar?.display) {
      const toastType = state?.request?.snackbar?.error ? "error" : "success";

      toast[toastType](state?.request?.snackbar?.message, {
        autoClose: 3000,
      });

      dispatch({
        type: Types.SetSnackbar,
        payload: { ...state?.request?.snackbar, display: false },
      });
    }
  }, [state?.request?.snackbar?.display]);

  return (
    <div className={css.detail}>
      {isLoading && <Loading />}

      <header className={css.detail_header}>
        <div className={css.headerTitle}>
          <Heading className={css.headerTitle_text} type="h3">
            {t("booking.booking-details.resume.title")}
          </Heading>

          {isLoadingDetails ? (
            <SkeletonBase width="80px" height="16px" radius="8px" />
          ) : (
            <Pill
              icon={utils.icons.statusIcon(booking?.status)}
              className={utils.icons.iconClassName(booking?.status)}
              {...utils.icons.statusPillColor(booking?.status)}
            >
              {utils.icons.iconText(booking?.status)}
            </Pill>
          )}
        </div>
      </header>

      {editing ? (
        <div className={css.detail_form}>
          <div className={css.formGroup}>
            <UserSelect className={css.formSelector} />

            <TextField
              className={css.formField}
              placeholder={t("booking.booking-request.form.event-title.placeholder")}
              label={t("booking.booking-request.form.event-title.label")}
              value={bookingTitle}
              maxLength={80}
              onChange={(e: any) => setBookingTitle(e?.target?.value)}
            />
          </div>

          <section className={css.formGroup}>
            <Select
              label={t("booking.booking-request.form.event-type.label")}
              placeholder={t("booking.booking-request.form.event-type.placeholder")}
              options={state?.request?.eventTypesOptions || []}
              value={selectableBookingTypeByIds[state?.request?.eventType?.value]}
              className={css.formSelector}
              maxMenuHeight={150}
              onChange={e =>
                dispatch({ type: Types.SetEventType, payload: { value: e?.value, error: false } })
              }
            />

            <ProgramSelect
              className={css.formSelector}
              initialValue={
                booking?.relatedProgram?.id
                  ? {
                      label: getProgramSelectorLabel(booking?.relatedProgram),
                      value: booking?.relatedProgram?.id,
                    }
                  : undefined
              }
            />
          </section>

          <Textarea
            className={css.formTextarea}
            label={t("booking.booking-request.form.description.label")}
            value={bookingDescription}
            onChange={value => {
              setDescriptionHasBeenChanged(true);
              setBookingDescription(value);
            }}
          />

          <section className={css.formActions}>
            <Button
              className={css.info_container__button}
              disabled={isLoading}
              variant="ghost"
              onClick={() => setEdit(false)}
            >
              {t("booking.booking-details.actions.cancel")}
            </Button>
            <Button disabled={isLoading || !hasChanges} color="primary" onClick={handleSaveChanges}>
              {t("booking.booking-details.actions.save")}
            </Button>
          </section>
        </div>
      ) : (
        <>
          <section className={css.detail_resume}>
            <div className={css.resumeItem}>
              <Text className={css.resumeItem_label}>
                {t("booking.booking-details.resume.responsible")}
              </Text>
              <Text className={css.resumeItem_value}>
                {isLoadingDetails ? (
                  <SkeletonBase width="100px" height="16px" />
                ) : !booking?.responsible ? (
                  "-"
                ) : (
                  `${booking?.responsible?.name ?? ""} ${booking?.responsible?.lastName ?? ""}`
                )}
              </Text>
            </div>
          </section>

          <section className={css.detail_resume}>
            <div className={css.resumeItem}>
              <Text className={css.resumeItem_label}>
                {t("booking.booking-details.resume.event-type")}
              </Text>
              <Text className={css.resumeItem_value}>
                {isLoadingDetails ? (
                  <SkeletonBase width="100px" height="16px" />
                ) : (
                  booking?.type?.name || "-"
                )}
              </Text>
            </div>

            <div className={css.resumeItem}>
              <Text className={css.resumeItem_label}>
                {t("booking.booking-details.resume.program")}
              </Text>
              <Text className={css.resumeItem_value}>
                {isLoadingDetails ? (
                  <SkeletonBase width="100px" height="16px" />
                ) : (
                  booking?.relatedProgram?.name || "-"
                )}
              </Text>
            </div>
          </section>

          <section className={css.detail_resume}>
            <div className={css.resumeItem}>
              <Text className={css.resumeItem_label}>
                {t("booking.booking-details.resume.description")}
              </Text>
              <Text className={css.resumeItem_value}>
                {isLoadingDetails ? (
                  <SkeletonBase width="100px" height="16px" />
                ) : (
                  booking?.description || "-"
                )}
              </Text>
            </div>
          </section>

          {isAdminBooking && (
            <Button
              className={css.detail_editBtn}
              disabled={isLoadingDetails}
              variant="secondary"
              onClick={() => {
                setBookingTitle(booking?.title || "");
                setBookingDescription(booking?.description || "");
                setDescriptionHasBeenChanged(false);
                setIgnoreMutationResult(true);
                setEdit(true);
              }}
            >
              {t("booking.booking-details.actions.edit-booking")}
            </Button>
          )}
        </>
      )}
    </div>
  );
};

export default memo(Detail);
